import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

export type Locale = 'zh' | 'en';

const STORAGE_KEY = 'forprojourney_locale';
const META_BY_LOCALE: Record<Locale, { title: string; description: string }> = {
  zh: {
    title: 'For Pro Journey - 欧洲旅行导航',
    description: 'ForProJourney - 全球旅行导航网站，为旅行者提供官方签证申请和景点门票购买入口'
  },
  en: {
    title: 'ForProJourney - Europe Travel Navigation',
    description: 'ForProJourney helps travelers find official visa application portals and attraction ticket websites worldwide.'
  }
};

interface LocaleContextType {
  locale: Locale;
  setLocale: (locale: Locale) => void;
}

const LocaleContext = createContext<LocaleContextType | null>(null);

function getStoredLocale(): Locale {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'zh' || stored === 'en') return stored;
  } catch (_) {}
  try {
    const candidates = navigator.languages?.length ? navigator.languages : [navigator.language];
    const normalized = candidates
      .filter(Boolean)
      .map((item) => item.toLowerCase());
    const hasChinese = normalized.some((item) => item.startsWith('zh'));
    return hasChinese ? 'zh' : 'en';
  } catch (_) {
    return 'en';
  }
}

export const LocaleProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [locale, setLocaleState] = useState<Locale>(getStoredLocale);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch (_) {}
  }, []);

  useEffect(() => {
    document.documentElement.lang = locale === 'zh' ? 'zh-CN' : 'en';
    const meta = META_BY_LOCALE[locale];
    document.title = meta.title;
    const desc = document.querySelector('meta[name="description"]');
    if (desc) {
      desc.setAttribute('content', meta.description);
    }
  }, [locale]);

  return (
    <LocaleContext.Provider value={{ locale, setLocale }}>
      {children}
    </LocaleContext.Provider>
  );
};

export function useLocale(): LocaleContextType {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error('useLocale must be used within LocaleProvider');
  return ctx;
}
